import posthog from 'posthog-js'
import { getErrorMessage } from '~/@lib/utils/error.utils'

function commonErrorHandler(
  error: ApiError,
  onDisplayErrorNotification: (errorMessage: string) => void,
  onUnauthorizedError: () => void,
  logoutOnFailedToFetch = false
) {
  if (!import.meta.env.PROD) {
    console.error('DEBUG: api request error', error)
  }

  if (_isUnauthorizedError(error)) {
    onUnauthorizedError()
    return
  }

  if (_isFailedToFetchError(error)) {
    if (logoutOnFailedToFetch) {
      onUnauthorizedError()
    }
    return
  }

  if (!_isUnexpectedError(error)) return

  // ApiError je plain objekt (nie Error) — obalíme, aby mal issue čitateľný message + stack.
  posthog.captureException(
    error instanceof Error
      ? error
      : new Error(getErrorMessage(error), { cause: error }),
    { api_error_status_code: _getStatusCode(error) }
  )

  const errorMessage = getErrorMessage(error)
  onDisplayErrorNotification(errorMessage)
}

type ApiError = {
  error?: {
    code?: number
    error?: string
  }
  status?: number
}

export { commonErrorHandler, type ApiError }

/**
 * Rozpozná, či je chyba neočakávaná a mala by sa poslať do PostHogu.
 *
 * Očakávané chyby (neriesime):
 * - 400 (Bad Request) - validácia, zlé dáta
 * - 401 (Unauthorized) - nie je prihlásený
 * - 403 (Forbidden) - nemá oprávnenie
 * - 404 (Not Found) - zdroj neexistuje
 *
 * Neočakávané chyby (riesime):
 * - 500+ (Server errors)
 * - Iné neočakávané chyby
 */
function _isUnexpectedError(error: ApiError): boolean {
  const statusCode = _getStatusCode(error)

  if (statusCode != null && typeof statusCode === 'number') {
    if (statusCode >= 500) {
      return true
    }

    return false
  }

  // ak neprisiel statusCode, tak radsej to povazujme za neocakavanu chyby - je to edge case, ale teda can happen
  return true
}

/**
 * Rozpozná, či je chyba neočakávaná a mala by sa poslať do PostHogu.
 *
 * Očakávané chyby (neriesime):
 * - 400 (Bad Request) - validácia, zlé dáta
 * - 401 (Unauthorized) - nie je prihlásený
 * - 403 (Forbidden) - nemá oprávnenie
 * - 404 (Not Found) - zdroj neexistuje
 *
 * Neočakávané chyby (riesime):
 * - 500+ (Server errors)
 * - Iné neočakávané chyby
 */
function _isUnauthorizedError(error: ApiError): boolean {
  const statusCode = _getStatusCode(error)
  return statusCode === 401
}

function _isFailedToFetchError(error: ApiError): boolean {
  const statusCode = _getStatusCode(error)

  // Network errors typically don't have a status code
  if (statusCode != null) return false

  // Check error message for network error patterns
  const errorMessage =
    (error as any).message?.toLowerCase() ??
    (error as any).error?.error?.toLowerCase() ??
    String(error).toLowerCase()

  return (
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('err_connection_refused') ||
    errorMessage.includes('networkerror') ||
    errorMessage.includes('network error')
  )
}

function _getStatusCode(error: ApiError): number | undefined {
  return error.error?.code ?? error.status
}
